import type { ChatRuntimeConfig } from '../types/chat'
import type { AuthenticatedFetch } from '../types/network'
import {
  ConversationApiError,
  type ConversationActivity,
  type CreatedShare,
  type SharedConversation,
  type SharedConversationPage,
  type SharedMessage,
  type SharePreview,
} from '../types/conversation'
import { requestJson, type PageOptions } from './conversations'

/**
 * Chat sharing. `aiServerBase` is the AI server's root (e.g.
 * `https://ai.dev-myhelp.eon.com`): the share routes live outside `/threads`.
 *
 * The share token only ever travels in a request body, never in a URL path, so
 * it cannot end up in an access log. The shareable link carries it after `#`.
 */

export function previewShare(
  authenticatedFetch: AuthenticatedFetch,
  aiServerBase: string,
  threadId: string,
): Promise<SharePreview> {
  return requestJson<SharePreview>(
    authenticatedFetch,
    `${aiServerBase}/threads/${encodeURIComponent(threadId)}/share-preview`,
  )
}

export function createShare(
  authenticatedFetch: AuthenticatedFetch,
  aiServerBase: string,
  threadId: string,
): Promise<CreatedShare> {
  return requestJson<CreatedShare>(
    authenticatedFetch,
    `${aiServerBase}/threads/${encodeURIComponent(threadId)}/share`,
    'POST',
    {},
  )
}

export function openShared(
  authenticatedFetch: AuthenticatedFetch,
  aiServerBase: string,
  token: string,
  { cursor, limit }: PageOptions = {},
): Promise<SharedConversationPage> {
  return requestJson<SharedConversationPage>(
    authenticatedFetch,
    `${aiServerBase}/shared/open`,
    'POST',
    {
      token,
      ...(cursor === undefined ? {} : { cursor }),
      ...(limit === undefined ? {} : { limit }),
    },
  )
}

export function forkShared(
  authenticatedFetch: AuthenticatedFetch,
  aiServerBase: string,
  token: string,
): Promise<{ threadId: string }> {
  return requestJson<{ threadId: string }>(
    authenticatedFetch,
    `${aiServerBase}/shared/fork`,
    'POST',
    { token },
  )
}

/**
 * Pages `openShared` to exhaustion and returns the snapshot oldest-first, the
 * same way `fetchWholeConversation` does. The whole history is needed because
 * the first reply forks the chat and then sends it to `/agent`, which checks
 * every message against what the server stored.
 *
 * A stale cursor (`400 PAGINATION_INVALID`) restarts from page 1 once.
 */
export async function fetchWholeShared(
  authenticatedFetch: AuthenticatedFetch,
  aiServerBase: string,
  token: string,
  limit = 100,
): Promise<SharedConversation> {
  const pages: SharedMessage[][] = []
  const activityPages: ConversationActivity[][] = []
  let first: SharedConversationPage | undefined
  let cursor: string | undefined
  let hasRestarted = false

  for (;;) {
    let page: SharedConversationPage
    try {
      page = await openShared(authenticatedFetch, aiServerBase, token, { cursor, limit })
    } catch (error) {
      const isStaleCursor =
        error instanceof ConversationApiError &&
        error.code === 'PAGINATION_INVALID' &&
        cursor !== undefined &&
        !hasRestarted

      if (!isStaleCursor) throw error

      pages.length = 0
      activityPages.length = 0
      first = undefined
      cursor = undefined
      hasRestarted = true
      continue
    }

    first ??= page
    pages.unshift(page.messages)
    activityPages.unshift(page.activities ?? [])

    if (!page.page.hasMore || !page.page.nextCursor) break
    cursor = page.page.nextCursor
  }

  if (first === undefined) {
    throw new ConversationApiError('INTERNAL_SERVER_ERROR', 'The shared chat returned no pages', 500)
  }

  return {
    share: first.share,
    messages: pages.flat(),
    activities: activityPages.flat(),
    ...(first.ownThreadId === undefined ? {} : { ownThreadId: first.ownThreadId }),
  }
}

/** The link a colleague opens. The token sits in the fragment, which browsers never send. */
export function shareLinkFor(token: string, origin = window.location.origin): string {
  return `${origin}/new-ai/shared#${token}`
}

/**
 * A sentence a person can act on for a failed share call. Branches on the
 * server's `error` code, never its message.
 */
export function describeShareError(error: unknown): string {
  if (error instanceof ConversationApiError) {
    switch (error.code) {
      case 'SHARE_NOT_FOUND':
      case 'SHARE_REQUEST_INVALID':
        return "This link isn't available. It may have been deleted."
      case 'SHARE_EXPIRED':
      case 'SHARE_REVOKED':
        return 'This link has expired or was revoked.'
      case 'SHARE_OWN_THREAD':
        return 'This is your own chat. Open it from Recent Chats to continue.'
      case 'THREAD_EMPTY':
        return "There's nothing to share yet."
      default:
        return error.message
    }
  }

  return error instanceof Error ? error.message : String(error)
}

/** The AI server root the share routes hang off, from the chat config. */
export function aiServerBaseFor(
  config: Pick<ChatRuntimeConfig, 'aiServerBase' | 'conversationsEndpoint'>,
): string {
  return config.aiServerBase ?? config.conversationsEndpoint.replace(/\/threads\/?$/, '')
}
